import React, { useEffect, useMemo, useState } from 'react';

import { Typography, Alert, Checkbox, Tooltip } from 'antd';
import { FormButton, FormEditButton, SectionWrapper } from 'common/components';
import ModalUpdateWalmartForm from './ModalUpdateWalmartForm';
import PostProductShortInfo from './common/PostProductShortInfo';

import {
  CloseCircleFilled,
  CheckCircleFilled,
  ExpandOutlined,
} from '@ant-design/icons';
import { green } from '@ant-design/colors';
import { useGetFormErrorsByProductId } from './hooks';

import ErrorListWithShowMore from './common/ErrorListWithShowMore';
import { isArray } from 'lodash';

const { Title } = Typography;

const PostProductList = ({
  selectedProducts,
  isFullView,
  needToRepublishList,
  setNeedToRepublishList,
}) => {
  return (
    <div
      style={{ marginTop: 8, gap: 6, display: 'flex', flexDirection: 'column' }}
    >
      {selectedProducts?.length > 0
        ? selectedProducts?.map((productItem) => {
            const productId = productItem?.['productId'] || productItem?.['id'];

            const mappedPostProductItem = {
              ...productItem,
              productId,
              thumbnail:
                productItem?.productMarketingImage || productItem?.thumbnail,
            };
            return (
              <PostToWalmartProductItem
                key={productId}
                postProductItem={mappedPostProductItem}
                isFullView={isFullView}
                needToRepublishList={needToRepublishList}
                setNeedToRepublishList={setNeedToRepublishList}
              />
            );
          })
        : null}
    </div>
  );
};

const PostToWalmartProductItem = ({
  postProductItem,
  isFullView,
  needToRepublishList,
  setNeedToRepublishList,
}) => {
  const [modalUpdateVisible, setModalUpdateVisible] = useState(false);

  const productId = postProductItem?.productId;

  const isNeedToRepublish = useMemo(
    () =>
      isArray(needToRepublishList) &&
      needToRepublishList?.find((val) => val?.productId === productId)
        ?.needToRepublish,
    [needToRepublishList, productId]
  );

  const toggleModalUpdate = () => {
    setModalUpdateVisible((prev) => !prev);
  };

  const handleChangeRepublish = (e) => {
    const newNeedToRepublish = needToRepublishList.map((val) =>
      val?.productId === productId
        ? { ...val, needToRepublish: e.target.checked }
        : val
    );

    setNeedToRepublishList(newNeedToRepublish);
  };

  const validationResult = useGetFormErrorsByProductId({
    productId,
  });

  const { isValid, dataErrors } = validationResult || {};

  const hasNoBrickCodeError = dataErrors?.find((dataErrorItem) => {
    const { fullKey } = dataErrorItem;
    if (fullKey?.toLowerCase() === 'productitem.brickcodename') {
      return true;
    }

    return false;
  });

  useEffect(() => {
    if (!isValid) {
      const newNeedToRepublish = needToRepublishList.map((val) =>
        val?.productId === productId ? { ...val, needToRepublish: false } : val
      );

      setNeedToRepublishList(newNeedToRepublish);
    }
  }, [isValid]);

  const disabledUpdateButton = hasNoBrickCodeError;

  return (
    <SectionWrapper
      title={
        <Title
          level={5}
          type={!isValid ? 'danger' : ''}
          style={{
            marginBottom: 0,
            color: isValid ? green[6] : '',
          }}
        >
          {`PRODUCT ID - ${productId}`}{' '}
          {isValid ? <CheckCircleFilled /> : <CloseCircleFilled />}
        </Title>
      }
    >
      <PostProductShortInfo
        postProductItem={postProductItem}
        validationResult={validationResult}
      />

      <div>
        {isValid === true ? (
          <Alert
            type='success'
            banner
            message={
              <div style={{ marginRight: 100 }}>The product data is valid</div>
            }
          />
        ) : isValid === false ? (
          <Alert
            type='error'
            banner
            message={
              <div style={{ marginRight: 100 }}>
                Please update to address all the errors listed below.
              </div>
            }
          />
        ) : null}
        <Tooltip title='When product validation fails, this checkbox is automatically unchecked and disabled.'>
          <Checkbox
            checked={isNeedToRepublish}
            style={{ marginTop: 4 }}
            onChange={handleChangeRepublish}
            disabled={!isValid}
          >
            Auto Republish when product is updated
          </Checkbox>
        </Tooltip>
        {validationResult?.dataErrors?.length > 0 && (
          <ErrorListWithShowMore errorList={validationResult?.dataErrors} />
        )}
      </div>
      <>
        <div
          style={{
            position: 'absolute',
            right: 12,
            top: -14,
            display: 'flex',
            gap: 4,
          }}
        >
          <FormButton
            text='Open product'
            onClick={() => {
              const url = window.location.origin + `/product/${productId}`;
              window.open(url, '_blank', 'noopener,noreferrer');
            }}
            icon={<ExpandOutlined />}
          />
          <FormEditButton
            text='Update'
            onClick={toggleModalUpdate}
            disabled={disabledUpdateButton}
          />
        </div>
        <ModalUpdateWalmartForm
          visible={modalUpdateVisible}
          postProductItem={postProductItem}
          toggleModalUpdate={toggleModalUpdate}
        />
      </>
    </SectionWrapper>
  );
};

export default PostProductList;
